import { useParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import {
    obtenerDocumentosExpedienteAgrupadosPorTipo,
    getDocumento,
    eliminarDocumentoExpediente,
    ActualizarFolioDocumentoExpediente,
} from '../../../api/Documentos';
import {
    Layout,
    Typography,
    Spin,
    message,
    Collapse,
    Empty,
    Button,
    Popconfirm,
    Modal,
    Card,
    Tooltip,
    Tag,
    Splitter,
    Grid,
    Flex
} from 'antd';
import {
    FilePdfOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    LoadingOutlined,
    DeleteOutlined,
    SaveOutlined,
    PrinterOutlined,
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Content } = Layout;
const { useBreakpoint } = Grid;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Etiquetas NI/OC/CONT/COT: cuatro por tarjeta, así que van más compactas
// que el Tag por defecto para no romper el ancho del panel lateral.
const tagCompactoStyle = {
    fontSize: 10,
    lineHeight: '16px',
    padding: '0 4px',
    marginInlineEnd: 0,
    maxWidth: '100%',
};

const VistaArchivoDua = () => {
    const { id, numero, anio } = useParams();
    const [documentosAgrupados, setDocumentosAgrupados] = useState({});
    const [selectedDocBlobUrl, setSelectedDocBlobUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [loading, setLoading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [folio, setFolio] = useState('');
    const [folioGuardado, setFolioGuardado] = useState('');
    const [documentoActualId, setDocumentoActualId] = useState(null);
    const [expedienteActualId, setExpedienteActualId] = useState(null);
    const [savingFolio, setSavingFolio] = useState(false);
    const [activeCollapseKey, setActiveCollapseKey] = useState(null);
    //const [siderCollapsed, setSiderCollapsed] = useState(false);
    const screens = useBreakpoint();
    const isMobile = !screens.md; // < 768px
    const splitDirection = isMobile ? "horizontal" : "vertical";

    const inputRef = useRef(null);
    const visorRef = useRef(null);
    const printIframeRef = useRef(null);

    // Imprime el PDF actualmente visible usando un iframe oculto + el motor de
    // impresión nativo del navegador (react-pdf renderiza a canvas, que no se
    // imprime bien; en cambio el blob del PDF sí se imprime con fidelidad).
    const limpiarIframeImpresion = () => {
        if (printIframeRef.current) {
            try { document.body.removeChild(printIframeRef.current); } catch (e) { /* ya removido */ }
            printIframeRef.current = null;
        }
    };

    const handleImprimir = () => {
        if (!selectedDocBlobUrl) return;
        // Un iframe nuevo por impresión: así reimprimir el mismo documento vuelve
        // a disparar onload. Se mantiene oculto hasta la próxima impresión o al
        // desmontar (quitarlo antes cancelaría el diálogo de impresión).
        limpiarIframeImpresion();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.visibility = 'hidden';
        iframe.onload = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (e) {
                message.error('No se pudo abrir el diálogo de impresión de este documento.');
            }
        };
        iframe.src = selectedDocBlobUrl;
        document.body.appendChild(iframe);
        printIframeRef.current = iframe;
    };

    useEffect(() => () => limpiarIframeImpresion(), []);

    useEffect(() => {
        const fetchDocumentos = async () => {
            try {
                const data = await obtenerDocumentosExpedienteAgrupadosPorTipo(id);
                setDocumentosAgrupados(data || {});
            } catch (error) {
                message.error('Error al obtener documentos agrupados');
            }
        };
        fetchDocumentos();
    }, [id]);

    const handleSeleccionarDocumento = async (documento_id, expediente_id) => {
        if (folio && folio !== folioGuardado) {
            Modal.confirm({
                title: 'Cambios no guardados',
                content: '¿Seguro que deseas cambiar de documento?',
                okText: 'Sí',
                cancelText: 'Cancelar',
                onOk: () => cargarDocumento(documento_id, expediente_id),
            });
            return;
        }
        cargarDocumento(documento_id, expediente_id);
    };

    const cargarDocumento = async (documento_id, expediente_id) => {
        setLoading(true);
        try {
            const blob = await getDocumento(documento_id);
            const url = URL.createObjectURL(blob);
            setSelectedDocBlobUrl(url);
            setZoomLevel(1.0);
            setNumPages(null);
            setDocumentoActualId(documento_id);
            setExpedienteActualId(expediente_id);

            const tipoEncontrado = Object.entries(documentosAgrupados).find(([tipo, docs]) =>
                docs.some(doc => doc.documento_id === documento_id)
            );
            if (tipoEncontrado) setActiveCollapseKey(tipoEncontrado[0]);

            let folioEncontrado = '';
            Object.values(documentosAgrupados).forEach(group => {
                group.forEach(doc => {
                    if (doc.documento_id === documento_id) {
                        folioEncontrado = doc.folio || '';
                    }
                });
            });
            setFolio(folioEncontrado);
            setFolioGuardado(folioEncontrado);
        } catch {
            message.error('Error al cargar el documento');
        } finally {
            setLoading(false);
        }
    };

    const handleEliminarDocumento = async expedienteId => {
        try {
            await eliminarDocumentoExpediente(expedienteId);
            message.success('Documento eliminado correctamente');
            const data = await obtenerDocumentosExpedienteAgrupadosPorTipo(id);
            setDocumentosAgrupados(data || {});
            setSelectedDocBlobUrl(null);
        } catch {
            message.error('Error al eliminar el documento');
        }
    };

    const handleWheelZoom = e => {
        if (e.ctrlKey) {
            e.preventDefault();
            setZoomLevel(z => (e.deltaY < 0 ? Math.min(z + 0.1, 3) : Math.max(z - 0.1, 0.5)));
        }
    };

    useEffect(() => {
        const ref = visorRef.current;
        if (ref) {
            ref.addEventListener('wheel', handleWheelZoom, { passive: false });
        }
        return () => {
            if (ref) {
                ref.removeEventListener('wheel', handleWheelZoom);
            }
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === 'Tab') {
                e.preventDefault();
                const grupos = Object.entries(documentosAgrupados);
                let actualGrupoIndex = -1;
                let actualDocIndex = -1;

                grupos.forEach(([_, documentos], i) => {
                    const index = documentos.findIndex(doc => doc.documento_id === documentoActualId);
                    if (index !== -1) {
                        actualGrupoIndex = i;
                        actualDocIndex = index;
                    }
                });

                if (actualGrupoIndex === -1) return;

                const grupoActual = grupos[actualGrupoIndex][1];
                if (actualDocIndex + 1 < grupoActual.length) {
                    const siguienteDoc = grupoActual[actualDocIndex + 1];
                    handleSeleccionarDocumento(siguienteDoc.documento_id, siguienteDoc.id);
                } else if (actualGrupoIndex + 1 < grupos.length) {
                    const siguienteGrupo = grupos[actualGrupoIndex + 1][1];
                    if (siguienteGrupo.length > 0) {
                        handleSeleccionarDocumento(siguienteGrupo[0].documento_id, siguienteGrupo[0].id);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [documentosAgrupados, documentoActualId, folio, folioGuardado]);

    const guardarFolio = async () => {
        if (!folio.trim() || !expedienteActualId) return;
        setSavingFolio(true);
        try {
            await ActualizarFolioDocumentoExpediente(expedienteActualId, folio.trim());
            setFolioGuardado(folio.trim());
            message.success('Folio actualizado correctamente');
        } catch {
            message.error('Error al actualizar folio');
        } finally {
            setSavingFolio(false);
        }
    };


    const sidebarContent = (
        <div className="p-4 pt-4">
            <Title level={5}>Archivo de DUA: {numero}-{anio}</Title>
            <Collapse
                accordion
                activeKey={activeCollapseKey}
                onChange={(key) => setActiveCollapseKey(key)}
                items={Object.entries(documentosAgrupados).map(([tipo, documentos]) => ({
                    key: tipo,
                    label: `${tipo} (${documentos.length})`,
                    children: (
                        <>
                            {documentos.map((doc) => {
                                const isSelected = doc.documento_id === documentoActualId;
                                const etiquetas = [
                                    { label: 'NI', value: doc.nota_ingreso, color: 'green' },
                                    { label: 'OC', value: doc.orden_compra, color: 'orange' },
                                    { label: 'CONT', value: doc.contrato, color: 'blue' },
                                    { label: 'COT', value: doc.cotizacion, color: 'purple' },
                                ].filter((etiqueta) => etiqueta.value);
                                return (
                                    <Card
                                        key={doc.id}
                                        size="small"
                                        className={`mb-2 p-2 transition-shadow cursor-pointer ${isSelected ? 'shadow-md border border-blue-500' : 'hover:shadow'} rounded-lg`}
                                        onClick={() => handleSeleccionarDocumento(doc.documento_id, doc.id)}
                                    >
                                        <div className="flex justify-between items-center gap-2">
                                            {/* Sección del ícono y texto */}
                                            <div className="flex flex-col overflow-hidden min-w-0 flex-1">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <FilePdfOutlined className="text-red-500 text-base shrink-0" />
                                                    {/* Nombre truncado con tooltip */}
                                                    <Tooltip title={doc.nombre_original}>
                                                        <Text
                                                            className="truncate max-w-full text-[13px] font-medium"
                                                            ellipsis
                                                        >
                                                            {doc.nombre_original}
                                                        </Text>
                                                    </Tooltip>
                                                </div>
                                                {etiquetas.length > 0 && (
                                                    <div className="flex gap-1 mt-1 flex-wrap">
                                                        {etiquetas.map(({ label, value, color }) => (
                                                            <Tooltip key={label} title={`${label}: ${value}`}>
                                                                <Tag color={color} style={tagCompactoStyle} className="truncate">
                                                                    {label}: {value}
                                                                </Tag>
                                                            </Tooltip>
                                                        ))}
                                                    </div>
                                                )}
                                                <Text type="secondary" className="text-[11px] mt-1 truncate max-w-full">
                                                    {doc.usuario}
                                                </Text>
                                            </div>

                                            {/* Botón eliminar con ícono */}
                                            <Popconfirm
                                                title="¿Eliminar documento?"
                                                onConfirm={(e) => {
                                                    e.stopPropagation();
                                                    handleEliminarDocumento(doc.id);
                                                }}
                                                onCancel={(e) => e.stopPropagation()}
                                                okText="Sí"
                                                cancelText="No"
                                            >
                                                <Button
                                                    danger
                                                    shape="circle"
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Popconfirm>
                                        </div>
                                    </Card>
                                );
                            })}
                        </>
                    ),
                }))}
            >


            </Collapse>
        </div>

    );

    const visorContent = (
        <Content ref={visorRef} className="p-2 h-full overflow-y-auto">
            {loading ? (
                <Spin indicator={<LoadingOutlined spin />} size="large" fullscreen />
            ) : selectedDocBlobUrl ? (
                <>
                    <div className="sticky top-0 z-10 bg-gray-100/80 backdrop-blur rounded-md p-2 border-b mb-4 flex flex-wrap items-center gap-x-2 gap-y-2">
                        {/* Controles de Zoom: − % + (agrupados) */}
                        <div className="flex gap-1 items-center flex-shrink-0">
                            <Tooltip title="Alejar">
                                <Button icon={<ZoomOutOutlined />} onClick={() => setZoomLevel(z => Math.max(z - 0.1, 0.5))} />
                            </Tooltip>
                            <span className="text-sm text-gray-700 text-center tabular-nums select-none w-12">{(zoomLevel * 100).toFixed(0)}%</span>
                            <Tooltip title="Acercar">
                                <Button icon={<ZoomInOutlined />} onClick={() => setZoomLevel(z => Math.min(z + 0.1, 3))} />
                            </Tooltip>
                        </div>

                        {/* Imprimir */}
                        <Tooltip title="Imprimir documento">
                            <Button icon={<PrinterOutlined />} onClick={handleImprimir} aria-label="Imprimir documento" />
                        </Tooltip>

                        {/* Folio: se alinea a la derecha en pantallas anchas y baja de línea en angostas */}
                        <div className="flex gap-2 items-center flex-shrink-0 ml-auto">
                            <input
                                ref={inputRef}
                                type="text"
                                value={folio}
                                onChange={(e) => setFolio(e.target.value)}
                                onKeyDown={async (e) => {
                                    if (e.key === 'Enter' && folio.trim()) await guardarFolio();
                                }}
                                placeholder="Folio"
                                aria-label="Folio del documento"
                                className="border border-gray-300 px-3 py-1.5 rounded-md w-20 sm:w-28 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <Tooltip title="Guardar folio">
                                <Button
                                    type="primary"
                                    disabled={!folio.trim()}
                                    loading={savingFolio}
                                    onClick={guardarFolio}
                                    icon={<SaveOutlined />}
                                    aria-label="Guardar folio"
                                />
                            </Tooltip>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <div className="w-fit">
                            <Document file={selectedDocBlobUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                {Array.from(new Array(numPages), (_, index) => (
                                    <Page key={index + 1} pageNumber={index + 1} scale={zoomLevel} />
                                ))}
                            </Document>
                        </div>
                    </div>

                </>
            ) : (
                <Empty description="Selecciona un documento para visualizarlo" className='my-auto' />
            )}
        </Content>
    );

    return (
        <Flex style={{ height: "100vh" }} vertical={isMobile}>
            {isMobile ? (
                <Splitter                    
                    style={{ height: "100vh", width: "100%" }}
                    layout="vertical"
                >
                    {/* Panel izquierdo (antes Sidebar) */}
                    <Splitter.Panel
                        min="20%"
                        max="30%"
                        defaultSize="25%"
                        collapsible={{ start: true, end: true, showCollapsibleIcon: true }}                        
                    >
                        {sidebarContent}
                    </Splitter.Panel>

                    {/* Panel derecho (antes Content) */}
                    <Splitter.Panel>
                        {visorContent}
                    </Splitter.Panel>
                </Splitter>) : (
                <Splitter
                    split={splitDirection}
                    style={{ height: "100vh", width: "100%" }}

                >
                    {/* Panel izquierdo (antes Sidebar) */}
                    <Splitter.Panel
                        min="20%"
                        max="30%"
                        defaultSize="25%"
                        collapsible={{ start: true, end: true, showCollapsibleIcon: true }}
                    >
                        {sidebarContent}
                    </Splitter.Panel>

                    {/* Panel derecho (antes Content) */}
                    <Splitter.Panel>
                        {visorContent}
                    </Splitter.Panel>
                </Splitter>
            )
            }
        </Flex>
    );
};

export default VistaArchivoDua;
